/**
 * Date rules a form definition can express (#1838, the wider half of #1111).
 *
 * Client mirror of `backend/src/services/dateRules.js`, pinned to it by
 * `backend/tests/contract/impossible-dates-parity.test.js`. Read that file for
 * the design; the rules below are the same data, and the algorithm is the same
 * two-pass evaluation, because the wizard blocking what the API accepts wastes
 * a family’s time and the API blocking what the wizard accepts costs them the
 * whole form.
 *
 * The wizard reads `problem`; the API reads `error`. One rule carries both so
 * the two sides cannot drift into different wordings for the same mistake.
 */
import type { DefinitionField } from './formDefinitions';

export type DateBound = 'past' | 'future';
export type DateCompareOp = 'not_before' | 'not_after';

export const DATE_BOUNDS: readonly DateBound[] = ['past', 'future'] as const;
export const DATE_COMPARE_OPS: readonly DateCompareOp[] = ['not_before', 'not_after'] as const;

export interface DateRule {
  section: string;
  key: string;
  bound?: DateBound;
  compare?: { op: DateCompareOp; section: string; key: string };
  /** The submit-blocking sentence the API lists. */
  error: string;
  /** The sentence the wizard attaches to the control. */
  problem: string;
}

/** Answers live at `${section}.${key}`, and `key` may itself be dotted. */
function readAnswer(data: Record<string, unknown>, section: string, key: string): unknown {
  return `${section}.${key}`
    .split('.')
    .reduce<unknown>(
      (node, k) => (node == null ? undefined : (node as Record<string, unknown>)[k]),
      data,
    );
}

function parseDateAnswer(value: unknown): number | null {
  if (typeof value !== 'string' || value.trim() === '') return null;
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? null : ms;
}

/** 23:59:59.999 UTC on `now`’s calendar day — see the server module. */
export function endOfDayUTC(now: Date): number {
  return Date.UTC(
    now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 23, 59, 59, 999,
  );
}

function boundFails(rule: DateRule, ms: number | null, endOfToday: number): boolean {
  if (ms === null || !rule.bound) return false;
  return rule.bound === 'past' ? ms > endOfToday : ms < endOfToday;
}

/**
 * Returns a map of `section.key` → message, the shape the wizard’s `problems`
 * map already speaks, so a bad date marks the control the family has to fix.
 */
export function dateRuleProblems(
  rules: readonly DateRule[],
  sectionData: Record<string, unknown>,
  now: Date = new Date(),
): Map<string, string> {
  const endOfToday = endOfDayUTC(now);
  const answer = (section: string, key: string) =>
    parseDateAnswer(readAnswer(sectionData, section, key));

  const failedBound = new Set<string>();
  for (const rule of rules) {
    if (boundFails(rule, answer(rule.section, rule.key), endOfToday)) {
      failedBound.add(`${rule.section}.${rule.key}`);
    }
  }

  const problems = new Map<string, string>();
  for (const rule of rules) {
    const path = `${rule.section}.${rule.key}`;
    if (problems.has(path)) continue;
    const mine = answer(rule.section, rule.key);
    if (boundFails(rule, mine, endOfToday)) {
      problems.set(path, rule.problem);
      continue;
    }
    if (!rule.compare || mine === null) continue;
    const otherPath = `${rule.compare.section}.${rule.compare.key}`;
    // One typo, one sentence.
    if (failedBound.has(path) || failedBound.has(otherPath)) continue;
    const theirs = answer(rule.compare.section, rule.compare.key);
    if (theirs === null) continue;
    const broken = rule.compare.op === 'not_before' ? mine < theirs : mine > theirs;
    if (broken) problems.set(path, rule.problem);
  }
  return problems;
}

/**
 * Read the rules a published version’s fields declare. Mirror of
 * `rulesFromFields` on the server, defaults included — a builder-authored rule
 * carries no sentences, and a comparison whose target cannot be named is
 * DROPPED rather than reported against a question nobody can find.
 */
export function rulesFromFields(fields: readonly DefinitionField[] = []): DateRule[] {
  const labels = new Map(fields.map((f) => [`${f.section_id}.${f.id}`, f.label || f.id]));
  const rules: DateRule[] = [];
  for (const f of fields) {
    if (f.retired) continue;
    const label = labels.get(`${f.section_id}.${f.id}`) as string;
    const bound = f.date_bound?.direction;
    if (bound && DATE_BOUNDS.includes(bound)) {
      rules.push({
        section: f.section_id,
        key: f.id,
        bound,
        error: f.date_bound?.message
          || (bound === 'past' ? `${label} is in the future` : `${label} is in the past`),
        problem: f.date_bound?.message
          || (bound === 'past'
            ? 'This date is in the future — please check it.'
            : 'This date is in the past — please check it.'),
      });
    }
    const cmp = f.date_compare;
    if (!cmp || !DATE_COMPARE_OPS.includes(cmp.op)) continue;
    const otherLabel = labels.get(`${cmp.section_id}.${cmp.field_id}`);
    if (!otherLabel) continue;
    const word = cmp.op === 'not_before' ? 'before' : 'after';
    rules.push({
      section: f.section_id,
      key: f.id,
      compare: { op: cmp.op, section: cmp.section_id, key: cmp.field_id },
      error: cmp.message || `${label} is ${word} ${otherLabel}`,
      problem: cmp.message || `This is ${word} ${otherLabel} — please check both dates.`,
    });
  }
  return rules;
}

/**
 * The three rules of #1111, in this shape. The exact sentences are pinned here
 * (not defaulted from labels) because they are the wording families and the
 * submit route have been reading since 2026-09-06, and #1838 requires them to
 * keep working. Twin of `SEEDED_DATE_RULES` in
 * `backend/src/services/impossibleDates.js`.
 */
export const SEEDED_DATE_RULES: readonly DateRule[] = Object.freeze([
  {
    section: 'patient',
    key: 'date_of_birth',
    bound: 'past',
    error: 'Date of birth is in the future',
    problem: 'This date is in the future — please check it.',
  },
  {
    section: 'patient',
    key: 'diagnosed_at',
    bound: 'past',
    error: 'Date of diagnosis is in the future',
    problem: 'This date is in the future — please check it.',
  },
  {
    section: 'patient',
    key: 'diagnosed_at',
    compare: { op: 'not_before', section: 'patient', key: 'date_of_birth' },
    error: 'Date of diagnosis is before the date of birth',
    problem: 'This is before the date of birth — please check both dates.',
  },
]);

/**
 * The #1111 rules, evaluated. Lives here rather than in `intakeFields.ts`
 * because that module is deliberately IMPORT-FREE — the seed generator bundles
 * it, and a dependency-free input is the safest thing to bundle — so it cannot
 * call the shared evaluator. Moving the function to the evaluator was the only
 * way to have one implementation rather than two.
 *
 * Client twin of the server's `impossibleDateErrors`
 * (`backend/src/services/impossibleDates.js`), pinned to it by
 * `backend/tests/contract/impossible-dates-parity.test.js`. The wizard blocks
 * the step; the API blocks the submit. Neither is decorative — a family can
 * reach submit with a stale draft that never passed through this screen.
 */
export function impossibleDateProblems(
  sectionData: Record<string, unknown>,
  now: Date = new Date(),
): Map<string, string> {
  return dateRuleProblems(SEEDED_DATE_RULES, sectionData, now);
}
